import axios, { AxiosError } from 'axios';
import { ApiError } from '@app/api/ApiError';
import { readToken, deleteToken, deleteUser } from '@app/services/localStorage.service';
import { notificationController } from '@app/controllers/notificationController';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';

export const httpApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

httpApi.interceptors.request.use((config) => {
  const token = readToken();
  if (token && token !== 'bearerToken') {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
  return config;
});

httpApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401) {
      deleteToken();
      deleteUser();
      // Redirect to login page
      if (window.location.pathname !== '/auth/login') {
        window.location.href = '/auth/login';
      }
    }

    // Handle 403 Forbidden - Insufficient permissions
    if (error.response?.status === 403) {
      notificationController.error({
        message: 'Yetkiniz yok',
        description: 'Bu işlem için gerekli yetkiye sahip değilsiniz.',
      });
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      notificationController.error({
        message: 'Bulunamadı',
        description: 'İstenen kaynak bulunamadı.',
      });
    }

    // Handle 409 Conflict
    if (error.response?.status === 409) {
      // Conflict errors are usually handled in components with specific messages
      // But we can show a generic message here as fallback
      const errorMessage = (error.response?.data as any)?.message || 'Bu kayıt zaten mevcut';
      notificationController.error({
        message: 'Çakışma',
        description: errorMessage,
      });
    }

    // Handle 422 Validation Error
    if (error.response?.status === 422) {
      const errorData = error.response?.data as any;
      const errorMessage = errorData?.message || 'Doğrulama hatası';
      notificationController.error({
        message: 'Doğrulama Hatası',
        description: errorMessage,
      });
    }

    // Handle 500 Server Error
    if (error.response?.status >= 500) {
      notificationController.error({
        message: 'Sunucu Hatası',
        description: 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
      });
    }

    const errorData: ApiErrorResponse = error.response?.data as ApiErrorResponse || {
      message: error.message || 'Bir hata oluştu',
    };

    throw new ApiError<ApiErrorData>(
      errorData.message || error.message || 'Bir hata oluştu',
      {
        message: errorData.message || error.message || 'Bir hata oluştu',
        statusCode: error.response?.status,
        errors: errorData.errors,
      }
    );
  }
);

export interface ApiErrorData {
  message: string;
  statusCode?: number;
  errors?: Record<string, string[]>; // Field-specific validation errors
}

export interface ApiErrorResponse {
  message: string;
  statusCode?: number;
  error?: string;
  errors?: Record<string, string[]>;
}
